import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * Session lifecycle event bus (Phase H Stage 2 / ADR-0046).
 *
 * The refresh interceptor emits `expired()` when the refresh path has
 * already failed. The application shell layout subscribes to `expired$`
 * and opens the session-expired modal in response.
 *
 * When the user submits valid credentials in the modal, AuthService.login
 * stores the new tokens and the dialog wiring calls `resumed(newAccess)`.
 * Pending failed requests that paused inside the interceptor's catchError
 * pick up the new access token and retry.
 *
 * `resumed(null)` signals user-cancelled re-login. The interceptor's
 * catchError propagates the original 401, the dialog redirects to /login.
 */
@Injectable({ providedIn: 'root' })
export class SessionEventsService {
  /** Emits when the refresh path has failed and the modal should open. */
  readonly expired$ = new Subject<void>();

  /**
   * Emits the new access token after successful re-authentication, or
   * `null` after the user cancelled. Interceptors take the first emission.
   */
  readonly resumed$ = new Subject<string | null>();

  expired(): void {
    this.expired$.next();
  }

  resumed(newAccessToken: string | null): void {
    this.resumed$.next(newAccessToken);
  }
}
